/**
 * Approvals tab — shows conversation-level approval requests inline.
 * Mirrors KC2's Approvals tab: list of all approval requests with
 * sender, approver, level, state, reviewed status, and remarks.
 */
import { useEffect, useState, type JSX } from 'react'
import { CheckCircle2, XCircle, Clock, ShieldCheck } from 'lucide-react'
import { api } from '../../../api/client.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { R } from './primitives.js'

interface ApprovalRequest {
  id: string
  senderName: string | null
  approverName: string | null
  approverType: number
  stateName: string | null
  levelName: string | null
  senderRemark: string | null
  reviewed: boolean
  dateReviewed: string | null
  reviewRemark: string | null
  dateSent: string
}

export interface ApprovalsTabProps {
  conversationId: string
}

export default function ApprovalsTab({ conversationId }: ApprovalsTabProps): JSX.Element {
  const [requests, setRequests] = useState<ApprovalRequest[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get<{ requests: ApprovalRequest[] }>(`/conversations/${conversationId}/approval-requests`)
      .then((res) => {
        if (!cancelled) setRequests(res.requests ?? [])
      })
      .catch(() => {
        if (!cancelled) setRequests([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [conversationId])

  if (loading) {
    return <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="rounded-full bg-slate-50 p-4">
          <ShieldCheck className="h-8 w-8 text-slate-300" strokeWidth={1.5} />
        </div>
        <p className="mt-3 text-sm font-medium text-slate-500">No approval requests</p>
      </div>
    )
  }

  const pending = requests.filter((r) => !r.reviewed)
  const reviewed = requests.filter((r) => r.reviewed)

  return (
    <div className="space-y-4 p-5">
      {pending.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-600">
            Pending ({pending.length})
          </div>
          <div className="space-y-3">
            {pending.map((req) => (
              <div key={req.id} className={`${R} border border-amber-200 bg-amber-50 p-3.5`}>
                <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                  <Clock className="h-3 w-3" />
                  Sent {formatDateTime(req.dateSent)}
                </div>
                <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-700">
                  <div><span className="font-medium">Sender:</span> {req.senderName ?? '—'}</div>
                  <div><span className="font-medium">Approver:</span> {req.approverName ?? '—'}</div>
                  <div><span className="font-medium">Level:</span> {req.levelName ?? '—'}</div>
                  <div><span className="font-medium">State:</span> {req.stateName ?? '—'}</div>
                </div>
                {req.senderRemark && (
                  <div className={`${R} border border-amber-100 bg-white px-3 py-2 text-sm text-slate-600`}>
                    <span className="font-medium">Remark:</span> {req.senderRemark}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {reviewed.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Reviewed ({reviewed.length})
          </div>
          <div className="space-y-3">
            {reviewed.map((req) => (
              <div
                key={req.id}
                className={`${R} border p-3.5 ${
                  req.stateName?.toLowerCase().includes('approve')
                    ? 'border-green-200 bg-green-50'
                    : req.stateName?.toLowerCase().includes('reject')
                      ? 'border-red-200 bg-red-50'
                      : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {req.stateName?.toLowerCase().includes('approve') ? (
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                  ) : req.stateName?.toLowerCase().includes('reject') ? (
                    <XCircle className="h-3 w-3 text-red-600" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 text-slate-400" />
                  )}
                  Reviewed {req.dateReviewed ? formatDateTime(req.dateReviewed) : ''}
                </div>
                <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-700">
                  <div><span className="font-medium">Sender:</span> {req.senderName ?? '—'}</div>
                  <div><span className="font-medium">Approver:</span> {req.approverName ?? '—'}</div>
                  <div><span className="font-medium">Level:</span> {req.levelName ?? '—'}</div>
                  <div><span className="font-medium">State:</span> {req.stateName ?? '—'}</div>
                </div>
                {req.senderRemark && (
                  <div className={`${R} mt-1 border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600`}>
                    <span className="font-medium">Sender remark:</span> {req.senderRemark}
                  </div>
                )}
                {req.reviewRemark && (
                  <div className={`${R} mt-1 border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600`}>
                    <span className="font-medium">Review remark:</span> {req.reviewRemark}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
